import { Injectable } from '@angular/core';
import { ProfileService } from 'src/app/profile/profile.service';
import { NavigationCursorServiceSettings } from '../navigation-cursor.model';

@Injectable({
  providedIn: 'root',
})
export class TouchNavigationService {
  progressBarElm: HTMLIonProgressBarElement;
  touchedItem: Element;
  touchFilterTimer = null;
  touchFilterTimerProgress = 0;

  settings: NavigationCursorServiceSettings; // read only

  private selectedItem: Element;
  private repetitionFilterTimer = null;

  constructor(
    private profile: ProfileService //private profile: ProfileService
  ) //private profile: ProfileService
  {
    // Listens to events emitted when a touch point starts on screen.
    window.addEventListener('touchstart', (event) => {
      this.onTouchMoved(event);
    });

    // Listens to events emitted when a touch point moves on screen.
    window.addEventListener('touchmove', (event) => {
      this.onTouchMoved(event);
    });

    // Listens to events emitted when a touch point is canceled.
    window.addEventListener('touchcancel', (event) => {
      this.onTouchCanceled(event);
    });

    // Listens to events emitted when a touch point leaves the screen.
    window.addEventListener('touchend', (event) => {
      this.onTouchEnded(event);
    });

    // Creates the progress bar element to depict the touch filter's progress.
    this.createProgressBarElm();
  }

  /**
   * Searches for a navigable item within the touched element tree. In other words, look for an element
   * tree that has the custom property data-grid-col.
   * @param touchedElement
   * @returns
   */
  searchForNavigableItem(touchedElement: Element) {
    let element = touchedElement;

    const elements = [];

    // The first five elements of the touched node tree
    for (let i = 0; i < 5; i++) {
      if(element){
        elements.push(element);
        element = element.parentElement;

      }else{
        break;
      }
    }

    return elements.find((ele) => ele && ele.dataset.hasOwnProperty('gridCol'));
  }

  /**
   * Handles when a touch point moves on screen.
   * @param event touch event
   */
  private onTouchMoved(event: TouchEvent) {
    // Gets the first touch point.
    // TODO: Consider more points to enhance the estimation of the point the user is touching.
    const touch = event.touches[0];

    // Gets the touched element from the touch point's coordinates.
    const touchedElement = document.elementFromPoint(
      touch.clientX,
      touch.clientY
    );

    // Gets the touched navigable item.
    const touchedNavItem = this.searchForNavigableItem(touchedElement);

    // Checks if the touched elment is navigable.
    if (touchedNavItem) {
      // Checks if the touched item was changed by looking the inner html.
      if (
        this.selectedItem &&
        touchedNavItem.innerHTML === this.selectedItem.innerHTML
      ) {
        // Do nothing if the touched item is exacly the selected item.
      } else if (
        !this.touchedItem ||
        touchedNavItem.innerHTML !== this.touchedItem.innerHTML
      ) {
        this.onTouchedItemChanged(touchedNavItem);
      }
    } else {
      this.dismissTouchedItem();
    }
  }

  /**
   * Handles when a touch point is canceled.
   * @param event touch event
   */
  private onTouchCanceled(event: TouchEvent) {
    this.dismissTouchedItem();
  }

  /**
   * Handles when a touch point leaves the screen.
   * @param event touch event
   */
  private onTouchEnded(event: TouchEvent) {
    this.dismissTouchedItem();
  }

  /**
   * Dismisses the touched item.
   */
  private dismissTouchedItem() {
    if (this.touchedItem) {
      this.touchedItem.classList.remove('app-cursor-highlight');
    }

    this.touchedItem = null;

    clearInterval(this.touchFilterTimer);

    this.progressBarElm.style.opacity = '0';
    this.touchFilterTimerProgress = 0;
    this.progressBarElm.value = 0;

    // Refreshes the repetition filter
    this.refreshRepetitionFilter();
  }

  /**
   * Handles when the touch item changes.
   * @param event touch event
   */
  private onTouchedItemChanged(elm: Element) {
    // Dismiss the previous touched item.
    this.dismissTouchedItem();

    // Updates for the new touched item.
    this.touchedItem = elm;

    this.progressBarElm.color = 'primary';
    this.progressBarElm.style.opacity = '1';

    this.touchedItem.classList.add('app-cursor-highlight');

    this.refreshTouchFilter();
  }

  /**
   * Handles when a touch filter timer finishes.
   * @param event touch event
   */
  private onTouchFilterFinished() {
    this.progressBarElm.color = 'success';

    if (this.touchedItem) {
      (this.touchedItem as any).click();
      this.touchedItem.classList.remove('app-cursor-highlight');
    }

    // this.touchedItem = null;

    this.selectedItem = this.touchedItem;
  }

  /**
   * Refreshes the touch filter's timer. The purpuse of this filter is to ensure a minimum time
   * that an item should be kept touch before being selected.
   * @param event touch event
   */
  private refreshTouchFilter() {

    if (!this.profile.navSettingsBundle?.bundleOptionsState?.touchFilterDuration) {
      this.onTouchFilterFinished();
      return;
    }

    // Calculates step used for the progress bar. The progress is updated every 50 ms.
    const step = 1 / ((this.profile.navSettingsBundle?.bundleOptionsState?.touchFilterDuration | 1) / 50);

    // Creates the touch filter timer.
    this.touchFilterTimer = setInterval(() => {
      // Updates the progress.
      this.touchFilterTimerProgress += step;
      this.progressBarElm.value = this.touchFilterTimerProgress;

      // Checks if the progress has reached the end.
      if (this.touchFilterTimerProgress > 1) {
        clearInterval(this.touchFilterTimer);
        this.onTouchFilterFinished();
      }
    }, 50);
  }

  /**
   * Refreshes the repetition filter's timer. The purpuse of this filter is to avoid repetition of the
   * same item that was just selected.
   */
  private refreshRepetitionFilter() {
    clearTimeout(this.repetitionFilterTimer);

    this.repetitionFilterTimer = setTimeout(() => {
      this.selectedItem = null;
    }, this.profile.navSettingsBundle?.bundleOptionsState?.touchRepetitionFilterDuration | 10);
  }

  /**
   * Creates the progress bar element that shows the touch filter's progress.
   */
  private createProgressBarElm() {
    this.progressBarElm = document.createElement('ion-progress-bar');
    document.body.appendChild(this.progressBarElm);

    // Places the progress bar just bellow de toolbar.
    this.progressBarElm.style.cssText =
      'position:absolute; top: 56px; opacity: 0;';
  }
}
